/* @flow */

import React from 'react';
import { autorun, observable } from 'mobx';
import { observer } from 'mobx-react';
import Localizable from 'lib/intl/localizable';
import Styled from 'styled';
import ScrollViewRevealable from 'lib/scroll/scroll-view-revealable';
import PhotoSwipeView from 'lib/images/photoswipe-view';
import PhotoSwipe from 'photoswipe/dist/photoswipe';
import PhotoSwipeUIDefault from 'photoswipe/dist/photoswipe-ui-default';
import defaultStyles from './photoswipe-gallery-view.scss';

type OnRef = (ref: HTMLDivElement, index: number) => void;
type OnClick = (event: Event, item: PhotoSwipeGalleryItem) => void;

export class PhotoSwipeGalleryItem {
    onRef: OnRef;
    onClick: OnClick;
    index: ?number;
    key: string;

    w: number;
    h: number;
    src: string;
    msrc: string;

    constructor({
        onRef,
        w,
        h,
        src,
        msrc,
        onClick,
        key,
        ...rest
    }: {
        onRef: OnRef,
        onClick: OnClick,
        w: number,
        h: number,
        src: string,
        msrc: string,
        key: ?string,
    }) {
        this.onClick = event => onClick(event, this);
        this.onRef = onRef;
        this.h = h;
        this.w = w;
        this.src = src;
        this.msrc = msrc;
        this.key = key || src;

        Object.assign(this, rest);
    }

    setIndex(index: number) {
        this.index = index;
    }

    setRef = (ref: ?HTMLDivElement) => {
        if (ref && this.index != null) {
            this.onRef(ref, this.index);
        }
    };
}

@Localizable()
@ScrollViewRevealable()
@Styled(defaultStyles)
@observer
class PhotoSwipeGalleryView<T> extends React.Component {
    ref = {};
    disposer: ?Function;
    photoSwipeElement: ?HTMLDivElement;
    photoSwipe: PhotoSwipe;

    @observable items = [];

    photoSwipeItem(item: T) {
        const options: Object = {
            onRef: this.setRefForIndex,
            onClick: this.onClick,
        };

        Object.assign(options, this.props.photoSwipeItem(item));
        return new PhotoSwipeGalleryItem(options);
    }

    updateItems = () => {
        const { items } = this.props;
        this.items.replace(items.map(item => this.photoSwipeItem(item)));
    };

    UNSAFE_componentWillMount() {
        this.updateItems();
    }

    componentDidMount() {
        this.disposer = autorun(this.updateItems);
    }

    componentWillUnmount() {
        if (this.disposer) {
            this.disposer();
        }

        this.ref = {};
    }

    setPhotoSwipeElement = (ref: ?HTMLDivElement) => {
        this.photoSwipeElement = ref;
    };

    getThumbBoundsFn = (index: number) => {
        const ref = this.ref[index];

        if (!ref) {
            return undefined;
        }

        let rect;

        const result = ref.querySelector('img');

        if (result) {
            rect = result.getBoundingClientRect();
        } else {
            rect = ref.getBoundingClientRect();
        }

        const pageYScroll = window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop;

        return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
    };

    setRefForIndex = (ref: HTMLDivElement, index: number) => {
        this.ref[index] = ref;
    };

    onClick = (event: Event, item: PhotoSwipeGalleryItem) => {
        if (this.photoSwipeElement) {
            const { index } = item;

            this.photoSwipe = new PhotoSwipe(this.photoSwipeElement, PhotoSwipeUIDefault, this.items.slice(), {
                index,
                showHideOpacity: true,
                getThumbBoundsFn: this.getThumbBoundsFn,
                ...this.props.options,
            });

            this.photoSwipe.init();
        }
    };

    render() {
        let { styles } = this.props;
        if (this.props.photoswipeGalleryStyles) {
            styles = this.props.photoswipeGalleryStyles;
        }

        const { limit, firstImageSizes, sizes } = this.props;

        const items = limit != null ? this.items.slice(0, limit) : this.items;

        return (
            <div className={styles.photoswipeGallery}>
                <ul className={styles.list}>
                    {items.map((item, index) => {
                        item.setIndex(index);

                        const itemProps = { item };
                        if (index === 0 && firstImageSizes) {
                            itemProps.sizes = firstImageSizes;
                        } else if (sizes) {
                            itemProps.sizes = sizes;
                        } else {
                            itemProps.sizes = { xs: 150 };
                        }

                        itemProps.srcSet = item.srcSet;

                        return (
                            <li ref={item.setRef} key={item.key} className={styles.item}>
                                {React.cloneElement(this.props.itemElement, itemProps)}
                            </li>
                        );
                    })}
                </ul>
                <PhotoSwipeView element={this.setPhotoSwipeElement} />
            </div>
        );
    }
}

export default PhotoSwipeGalleryView;
